/** /external_access/ — the per-call External_Access_Log (dev-platform metaplan G2; GATE P2).
 *
 *  One row per external call: every request authenticated by a sandbox or partner key, every
 *  MCP call, every docs / developer-portal request. The table is monthly partitioned and kept
 *  13 months, so — like /api_requests/ — a `created` time bound is MANDATORY in `filters` (the
 *  backend answers 400 without one; it prunes the partitions). `partner_id`, `credential_id`
 *  and `tour_operator_id` are authoritative scopes ANDed on top: the partner and credential
 *  activity tabs use them. Ordering: `id` | `created_ts` ('-' desc; default `-created_ts`).
 *
 *  Principal columns are plain ids — a row outlives its key, user and partner — so the
 *  `credential` / `partner` / `user` / `tenant` refs are null when that record is gone while
 *  the `*_id` stays. Shapes come from the generated spec types. */
import { apiGet, extendedListConfig } from './client';
import type { CountMode, CountOnlyResult } from './countStatus';
import type { components } from './types';
import type { PaginatedResponse } from './users';
import type { WireNode } from '@/components/QuerySearch';
import type { TenantIdsParam } from '@/scope/tenantIds';

export type ExternalAccessItem = components['schemas']['ConsoleExternalAccessItem'];
export type ExternalAccessSummary = components['schemas']['ConsoleExternalAccessSummary'];
export type ExternalAccessChannel = ExternalAccessItem['channel'];
export type ExternalAccessOutcome = ExternalAccessItem['outcome'];
export type ExternalAccessPrincipalKind = ExternalAccessItem['principal_kind'];

export type ExternalAccessListResponse = PaginatedResponse<ExternalAccessItem> & {
  /** Present in `count_mode=sync` only (the sync count is capped). */
  count_is_capped?: boolean;
};

/** The three authoritative scopes; at most one is used per caller. The summary takes
 *  exactly one operator. */
export interface ExternalAccessScope {
  partner_id?: number;
  credential_id?: number;
  tour_operator_id?: number;
}

export interface ExternalAccessListParams extends Omit<ExternalAccessScope, 'tour_operator_id'> {
  /** One operator or a comma list (`'12,845'`); the list endpoint takes several. */
  tour_operator_id?: TenantIdsParam;
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  page_size?: number;
  ordering?: string;
  /** MUST carry a `created` bound. */
  filters?: WireNode[];
  /** Override the endpoint's own row-total mode; omit to keep the server default. */
  count_mode?: CountMode;
  count_only?: boolean;
}

function toQuery(p: ExternalAccessListParams): string {
  const usp = new URLSearchParams();
  if (p.page) usp.set('page', String(p.page));
  if (p.page_size) usp.set('page_size', String(p.page_size));
  if (p.ordering) usp.set('ordering', p.ordering);
  if (p.timeout) usp.set('timeout', p.timeout);
  if (p.partner_id !== undefined) usp.set('partner_id', String(p.partner_id));
  if (p.credential_id !== undefined) usp.set('credential_id', String(p.credential_id));
  if (p.tour_operator_id !== undefined) usp.set('tour_operator_id', String(p.tour_operator_id));
  // Omit `filters` entirely when empty (never send `filters=[]`).
  if (p.filters && p.filters.length > 0) usp.set('filters', JSON.stringify(p.filters));
  if (p.count_mode) usp.set('count_mode', p.count_mode);
  if (p.count_only) usp.set('count_only', '1');
  return usp.toString();
}

export async function list(p: ExternalAccessListParams = {}): Promise<ExternalAccessListResponse> {
  const qs = toQuery(p);
  return apiGet(`/external_access/${qs ? `?${qs}` : ''}`, extendedListConfig(p.timeout));
}

/** Total only, for the grid's on-demand row count (`async`: exact, uncapped, off-request). */
export async function count(p: ExternalAccessListParams = {}): Promise<CountOnlyResult> {
  const qs = toQuery({ ...p, count_only: true, count_mode: 'async' });
  return apiGet(`/external_access/?${qs}`);
}

/** GET /external_access/summary/ — exactly one scope; `days` 1–90 (default 30) ending now.
 *  `totals.last_seen` is the latest call of any time, not only in the window. */
export async function summary(scope: ExternalAccessScope, days = 30): Promise<ExternalAccessSummary> {
  const usp = new URLSearchParams();
  if (scope.partner_id !== undefined) usp.set('partner_id', String(scope.partner_id));
  if (scope.credential_id !== undefined) usp.set('credential_id', String(scope.credential_id));
  if (scope.tour_operator_id !== undefined) usp.set('tour_operator_id', String(scope.tour_operator_id));
  usp.set('days', String(days));
  return apiGet(`/external_access/summary/?${usp.toString()}`);
}
